import React, { useState, useRef, useEffect } from "react"
import { RESERVATION_COLORS } from "@/constants/reservations"
import i18n from "@/i18n"

type CustomerStats = {
  reservations: {
    total: number
    pending: number
    seated: number
    noShow: number
    cancelled: number
  }
}

const CHART_DATA = [
  { key: "pending", color: RESERVATION_COLORS.pending.main, labelKey: "app.reservation.status.pending" },
  { key: "seated", color: RESERVATION_COLORS.seated.main, labelKey: "app.reservation.status.seated" },
  { key: "noShow", color: RESERVATION_COLORS.no_show.main, labelKey: "app.reservation.status.no_show" },
  { key: "cancelled", color: RESERVATION_COLORS.cancelled.main, labelKey: "app.reservation.status.cancelled" },
]
const OTHER_COLOR = "#D0D0D0"

interface PieChartProps {
  data: { value: number; color: string; label: string }[]
  radius?: number
  lockedIndex: number | null
  hoveredIndex: number | null
  onHover: (index: number | null) => void
  onLock: (index: number | null) => void
}

const PieChart: React.FC<PieChartProps> = ({ data, radius = 40, lockedIndex, hoveredIndex, onHover, onLock }) => {
  const total = data.reduce((acc, item) => acc + item.value, 0)
  if (total === 0) return null

  const size = radius * 2
  const center = radius

  let startAngle = -90

  const segments = data
    .filter((item) => item.value > 0)
    .map((item, originalIndex) => {
      const angle = (item.value / total) * 360
      const endAngle = startAngle + angle
      const largeArcFlag = angle > 180 ? 1 : 0

      const startRad = (startAngle * Math.PI) / 180
      const endRad = (endAngle * Math.PI) / 180

      const x1 = center + radius * Math.cos(startRad)
      const y1 = center + radius * Math.sin(startRad)
      const x2 = center + radius * Math.cos(endRad)
      const y2 = center + radius * Math.sin(endRad)

      const pathData =
        angle === 360
          ? `M ${center} ${center - radius} A ${radius} ${radius} 0 1 1 ${center - 0.001} ${center - radius} Z`
          : `M ${center} ${center} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`

      const midAngle = startAngle + angle / 2
      const midRad = (midAngle * Math.PI) / 180
      const tooltipX = center + radius * 0.6 * Math.cos(midRad)
      const tooltipY = center + radius * 0.6 * Math.sin(midRad)

      const segment = {
        path: pathData,
        color: item.color,
        label: item.label,
        value: item.value,
        index: originalIndex,
        tooltipX,
        tooltipY,
      }

      startAngle = endAngle
      return segment
    })

  const activeIndex = lockedIndex !== null ? lockedIndex : hoveredIndex
  const activeSegment = activeIndex !== null ? segments.find((s) => s.index === activeIndex) : null

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="overflow-visible">
      {segments.map((segment) => (
        <path
          key={segment.index}
          d={segment.path}
          fill={segment.color}
          className="cursor-pointer transition-opacity"
          opacity={activeIndex !== null && activeIndex !== segment.index ? 0.5 : 1}
          onMouseEnter={() => lockedIndex === null && onHover(segment.index)}
          onMouseLeave={() => lockedIndex === null && onHover(null)}
          onClick={(e) => {
            e.stopPropagation()
            onLock(lockedIndex === segment.index ? null : segment.index)
          }}
        />
      ))}
      {activeSegment && (
        <g pointerEvents="none">
          <rect
            x={activeSegment.tooltipX - 40}
            y={activeSegment.tooltipY - 12}
            width={80}
            height={24}
            rx={4}
            fill="rgba(0,0,0,0.8)"
          />
          <text
            x={activeSegment.tooltipX}
            y={activeSegment.tooltipY + 4}
            textAnchor="middle"
            fill="white"
            fontSize={10}
          >
            {activeSegment.label}: {activeSegment.value}
          </text>
        </g>
      )}
    </svg>
  )
}

const Chart = ({ stats }: { stats: CustomerStats }) => {
  const [lockedIndex, setLockedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setLockedIndex(null)
      }
    }

    document.addEventListener("click", handleClickOutside)
    return () => document.removeEventListener("click", handleClickOutside)
  }, [])

  const items = CHART_DATA.map((item, index) => ({
    value: stats.reservations[item.key],
    color: item.color,
    label: i18n.t(item.labelKey),
    index,
  }))

  const total = stats.reservations.total
  const knownTotal = items.reduce((acc, item) => acc + item.value, 0)
  const other = total - knownTotal

  if (other > 0) {
    items.push({
      value: other,
      color: OTHER_COLOR,
      label: i18n.t("app.clients.client_modal.chart.other"),
      index: items.length,
    })
  }

  const nonZeroItems = items.filter((item) => item.value > 0)

  if (total === 0 || nonZeroItems.length === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className="flex flex-row items-center justify-between w-full"
      data-testid="panel-reservations-edit-form-chart"
    >
      <div className="flex flex-col gap-1 text-sm text-gray-600">
        {nonZeroItems.map((item) => (
          <span key={item.index}>
            {item.label}: <span className="font-semibold">{item.value}</span>
          </span>
        ))}
      </div>
      <div className="flex-shrink-0">
        <PieChart
          data={items}
          radius={50}
          lockedIndex={lockedIndex}
          hoveredIndex={hoveredIndex}
          onHover={setHoveredIndex}
          onLock={setLockedIndex}
        />
      </div>
    </div>
  )
}

export default Chart
